import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { api, errMsg, money, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/Badges";
import { Stars } from "@/components/Rating";
import { t } from "@/lib/i18n";

async function act(path, msg, onChange, refresh) {
  try {
    await api.post(path);
    toast.success(msg);
    onChange();
    refresh?.();
  } catch (e) { toast.error(errMsg(e)); }
}

export function ShipperBids({ load, onChange }) {
  const bids = load.bids || [];
  return (
    <div className="hf-card" data-testid="shipper-bids-panel">
      <div className="px-5 py-4 border-b flex justify-between items-center">
        <p className="font-display font-bold">{t("Bids received")}</p>
        <span className="font-mono text-xs text-slate-500">{bids.length}</span>
      </div>
      {bids.length === 0 && <p className="p-5 text-sm text-slate-500">{t("No bids yet. Verified transporters will see your load on the board.")}</p>}
      <ul className="divide-y">
        {bids.map((b) => {
          const diff = b.price_per_mt - load.price_per_mt;
          return (
            <li key={b.id} className="p-5" data-testid={`bid-row-${b.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div>
                  <p className="font-mono text-xs text-slate-500">{b.transporter_code} · {fmtTime(b.created_at)}</p>
                  <Stars rating={b.transporter_rating} testId={`bid-rating-${b.id}`} />
                  <p className="font-mono text-xl font-bold mt-1">{money(b.price_per_mt, load.currency || "USD")}<span className="text-xs text-slate-500 font-normal"> {t("/mt")}</span></p>
                  <p className={`text-xs font-semibold ${diff > 0 ? "text-red-600" : diff < 0 ? "text-emerald-600" : "text-slate-500"}`}>
                    {diff === 0 ? t("Accepts your rate") : `Counter-bid ${diff > 0 ? "+" : ""}${money(diff, load.currency || "USD")} vs your rate`}
                  </p>
                </div>
                <StatusBadge status={b.status} testId={`bid-status-${b.id}`} />
              </div>
              {b.note && <p className="text-sm text-slate-600 mt-2 break-words">"{b.note}"</p>}
              {b.status === "pending" && load.status === "open" && !bids.some((x) => x.status === "shipper_accepted") && (
                <div className="flex gap-2 mt-3">
                  <button onClick={() => act(`/bids/${b.id}/accept`, t("Bid accepted — waiting for transporter to confirm"), onChange)} className="hf-btn-primary h-9 px-4" data-testid="btn-accept-bid-load"><Check className="w-4 h-4" /> {t("Accept")}</button>
                  <button onClick={() => act(`/bids/${b.id}/reject`, t("Bid declined"), onChange)} className="hf-btn-ghost h-9 px-4" data-testid="btn-reject-bid"><X className="w-4 h-4" /> {t("Decline")}</button>
                </div>
              )}
              {b.status === "shipper_accepted" && <p className="text-xs text-blue-700 mt-2">{t("You accepted. Waiting for the transporter to confirm.")}</p>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function TransporterBidBox({ load, onChange }) {
  const { user, refresh } = useAuth();
  const [price, setPrice] = useState(load.price_per_mt);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const b = load.my_bid;

  if (b && b.status !== "withdrawn") {
    return (
      <div className="hf-card p-5 space-y-3" data-testid="my-bid-box">
        <div className="flex justify-between items-center"><p className="font-display font-bold">{t("Your bid")}</p><StatusBadge status={b.status} testId="my-bid-status" /></div>
        <p className="font-mono text-2xl font-bold">{money(b.price_per_mt, load.currency || "USD")}<span className="text-xs text-slate-500 font-normal"> {t("/mt")}</span></p>
        {b.status === "shipper_accepted" && (
          <>
            <p className="text-sm text-blue-700">{t("The shipper accepted your bid. Confirm to unlock contact details and papers.")}</p>
            <div className="flex gap-2">
              <button onClick={() => act(`/bids/${b.id}/confirm`, t("Connection confirmed!"), onChange)} className="hf-btn-primary flex-1" data-testid="btn-confirm-bid">{t("Confirm & connect")}</button>
              <button onClick={() => act(`/bids/${b.id}/decline`, t("Bid withdrawn"), onChange)} className="hf-btn-ghost" data-testid="btn-withdraw-bid">{t("Withdraw")}</button>
            </div>
          </>
        )}
        {b.status === "confirmed" && <Link to={`/connections/${b.id}`} className="hf-btn-dark w-full">{t("View connection")}</Link>}
      </div>
    );
  }
  if (load.status !== "open") return <div className="hf-card p-5 text-sm text-slate-500">{t("This load is no longer open for bidding.")}</div>;

  const submit = async (p) => {
    setBusy(true);
    try {
      await api.post(`/loads/${load.id}/bids`, { price_per_mt: Number(p), note });
      toast.success(t("Bid placed — 1 credit used"));
      refresh();
      onChange();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const blocked = user.status !== "approved" || user.credits < 1;
  return (
    <div className="hf-card p-5 space-y-4" data-testid="bid-form">
      <p className="font-display font-bold">{t("Place a bid · 1 credit")}</p>
      {blocked && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">{user.status !== "approved" ? t("Account approval required before bidding.") : <>{t("No credits left.")} <Link to="/wallet" className="underline font-semibold">{t("Buy credits")}</Link></>}</p>}
      <button disabled={busy || blocked} onClick={() => submit(load.price_per_mt)} className="hf-btn-dark w-full" data-testid="btn-accept-shipper-rate">{t("Accept shipper rate ·")} {money(load.price_per_mt, load.currency || "USD")}{t("/mt")}</button>
      <div className="flex items-center gap-3 text-xs font-mono text-slate-400"><span className="h-px bg-slate-200 flex-1" />{t("OR COUNTER-BID")}<span className="h-px bg-slate-200 flex-1" /></div>
      <div>
        <label className="hf-label">{t("Your price per MT (")}{load.currency || "USD"})</label>
        <input type="number" min="0.01" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="hf-input font-mono" data-testid="input-counter-bid-price" />
      </div>
      <div>
        <label className="hf-label">{t("Note (optional)")}</label>
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="hf-input" placeholder={t("Truck availability, fleet size…")} data-testid="input-counter-bid-note" />
      </div>
      <button disabled={busy || blocked || !(Number(price) > 0)} onClick={() => submit(price)} className="hf-btn-primary w-full" data-testid="btn-submit-counter-bid">{t("Submit counter-bid")}</button>
    </div>
  );
}
